import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaTicketAlt, FaCalendarCheck, FaHistory, FaQrcode, FaEyeSlash } from "react-icons/fa";
import TicketCard from "../../components/Ticketing/TicketCard";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import { getMyTickets } from "../../services/ticketingService";
import "./MyTickets.css";

/**
 * Customer ticket wallet — all tickets for a loyalty account.
 * Route: /my-tickets?customer_token=...
 * Can also be accessed via email link.
 */
export default function MyTickets() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customerToken: authToken, viewMode, triggerLogin } = useAuth();

  // Prefer token from URL (email link) over session token
  const tokenFromUrl = searchParams.get("customer_token");
  const token = tokenFromUrl || authToken;
  // Optional event filter (from EventDetail "View My Tickets" button)
  const eventIdFilter = searchParams.get("event_id")
    ? parseInt(searchParams.get("event_id"), 10)
    : null;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Set of event_ids whose QR codes are expanded
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  const toggleExpand = (eventId) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const data = await getMyTickets(token);
        const all = Array.isArray(data) ? data : [];
        if (mounted) {
          // Apply optional event filter
          setTickets(eventIdFilter ? all.filter((t) => t.event_id === eventIdFilter) : all);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError("Could not load your tickets.");
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [token, eventIdFilter]);

  if (loading) return <LoadingPage />;

  if (!token) {
    return (
      <div className="mt-page"><div className="mt-container">
        <div className="mt-no-auth">
          <FaTicketAlt className="mt-no-auth-icon" />
          <h2>Sign in to view your tickets</h2>
          <p>Your tickets are linked to your Block Club account.</p>
          <button className="mt-login-btn" onClick={triggerLogin}>
            Sign In
          </button>
        </div>
      </div></div>
    );
  }

  // Split into upcoming and past, then group each by event
  const now = new Date();
  const upcoming = tickets.filter((t) => !t.start_at || new Date(t.start_at) >= now);
  const past = tickets.filter((t) => t.start_at && new Date(t.start_at) < now);

  const groupByEvent = (list) => {
    const map = new Map();
    list.forEach((t) => {
      if (!map.has(t.event_id)) map.set(t.event_id, []);
      map.get(t.event_id).push(t);
    });
    return map;
  };

  const STATUS_ORDER = { valid: 0, used: 1, cancelled: 2, transferred: 3 };
  const sortTickets = (list) =>
    [...list].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  const renderGroup = (map, showBuyMore = false) =>
    [...map.entries()].map(([eventId, eventTickets]) => {
      const isExpanded = expandedEvents.has(eventId);
      const sorted = sortTickets(eventTickets);
      const hasMultiple = sorted.length > 1;
      const eventSlug = sorted[0]?.event_slug;
      return (
        <div key={eventId} className="mt-event-group">
          <div className="mt-event-group-header">
            <span className="mt-event-group-title">
              {sorted[0].event_title}{hasMultiple ? ` · ${sorted.length} tickets` : ""}
            </span>
            <div className="mt-event-group-actions">
              {showBuyMore && eventSlug && (
                <button
                  className="mt-buy-more-btn"
                  onClick={() => navigate(`/events/${eventSlug}/tickets`)}
                >
                  + Buy more
                </button>
              )}
              {hasMultiple && (
                <button
                  className="mt-expand-btn"
                  onClick={() => toggleExpand(eventId)}
                >
                  {isExpanded ? <><FaEyeSlash /> Hide QR Codes</> : <><FaQrcode /> Show All QR Codes</>}
                </button>
              )}
            </div>
          </div>
          <div className="mt-ticket-list">
            {sorted
              .filter((ticket) => !isExpanded || ticket.status !== "cancelled")
              .map((ticket) => (
                <TicketCard
                  key={ticket.ticket_id}
                  ticket={ticket}
                  onClick={() => navigate(`/tickets/${ticket.ticket_code}`)}
                  expanded={isExpanded && ticket.status === "valid"}
                />
              ))}
          </div>
        </div>
      );
    });

  return (
    <div className="mt-page">
    <div className="mt-container">
      <div className="mt-header">
        <FaTicketAlt className="mt-header-icon" />
        <div>
          <h1>My Tickets</h1>
          {eventIdFilter && tickets.length > 0 && (
            <p className="mt-event-filter-label">{tickets[0].event_title}</p>
          )}
        </div>
      </div>

      {error && <div className="mt-error">{error}</div>}

      {tickets.length === 0 && !error && (
        <div className="mt-empty">
          <FaTicketAlt className="mt-empty-icon" />
          <h2>No tickets yet</h2>
          <p>Tickets you purchase will appear here.</p>
          <button className="mt-browse-btn" onClick={() => navigate("/")}>
            Browse Events
          </button>
        </div>
      )}

      {upcoming.length > 0 && (
        <section className="mt-section">
          <h2 className="mt-section-title">
            <FaCalendarCheck /> Upcoming
          </h2>
          {renderGroup(groupByEvent(upcoming), true)}
        </section>
      )}

      {past.length > 0 && (
        <section className="mt-section">
          <h2 className="mt-section-title mt-section-title--past">
            <FaHistory /> Past Events
          </h2>
          {renderGroup(groupByEvent(past))}
        </section>
      )}
    </div>
    </div>
  );
}
